package org.catharsis.spendtel.feature.presentation.expensereport

import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.data.ExpenseReportStatus

@Composable
fun ExpenseReportStatusChip(
    status: String,
    modifier: Modifier = Modifier,
) {
    val (label, containerColor, contentColor) = statusChipColors(status)

    Surface(
        modifier = modifier,
        color = containerColor,
        contentColor = contentColor,
        shape = RoundedCornerShape(999.dp),
    ) {
        Text(
            text = label.uppercase(),
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.6.sp,
        )
    }
}

@Composable
fun ExpenseReportStatusChipCompact(
    status: String,
    modifier: Modifier = Modifier,
) {
    val (label, containerColor, contentColor) = statusChipColors(status)

    Surface(
        modifier = modifier,
        color = containerColor,
        contentColor = contentColor,
        shape = RoundedCornerShape(999.dp),
    ) {
        Text(
            text = label.uppercase(),
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
        )
    }
}

@Composable
private fun statusChipColors(status: String): Triple<String, Color, Color> {
    return when {
        ExpenseReportStatus.isComplete(status) ->
            Triple(
                "Complete",
                SpendTelReportColors.mint.copy(alpha = 0.16f),
                SpendTelReportColors.mint.darkenForChip(),
            )
        ExpenseReportStatus.isFailed(status) ->
            Triple(
                "Failed",
                SpendTelReportColors.increase.copy(alpha = 0.14f),
                SpendTelReportColors.increase.darkenForChip(),
            )
        status.lowercase() == "parsing" ->
            Triple(
                "Analyzing",
                SpendTelReportColors.violet.copy(alpha = 0.14f),
                SpendTelReportColors.violet.darkenForChip(),
            )
        else ->
            Triple(
                ExpenseReportStatus.displayLabel(status),
                SpendTelReportColors.amber.copy(alpha = 0.16f),
                SpendTelReportColors.amber.darkenForChip(),
            )
    }
}

fun reportStatusMessage(status: String): String {
    return when (status.lowercase()) {
        "parsing" -> "We're reading your statements and shaping your monthly story and financial personality."
        "documents_loaded" -> "Your statements are in. The narrative is about to begin."
        "processing", "pending", "in_progress" ->
            "Your next chapter is loading. This can take up to 90 seconds."
        else -> when {
            ExpenseReportStatus.isComplete(status) ->
                "Your spending story is ready to explore."
            ExpenseReportStatus.isFailed(status) ->
                "Something went wrong while writing your story."
            else -> "Report status: ${ExpenseReportStatus.displayLabel(status)}"
        }
    }
}

private fun Color.darkenForChip(): Color {
    return Color(red = red * 0.72f, green = green * 0.72f, blue = blue * 0.72f, alpha = alpha)
}
